import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    HostListener,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ChannelFns } from 'icreate/common/models/channel';
import {
    ALARM_OPTION,
    ElementConditionLogicViewData,
    FAILURE,
    LOGIC_OPERATOR,
    LogicOperator,
} from 'icreate/common/models/element-condition-logic';
import { fromEvent, Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-condition-logic-input',
    templateUrl: './condition-logic-input.component.html',
    styleUrls: ['./condition-logic-input.component.css'],
    standalone: false,
})
export class ConditionLogicInputComponent implements OnInit, OnChanges, OnDestroy {
    readonly SENSOR_FAILURE = FAILURE;
    readonly ALARM_OPTION = ALARM_OPTION;
    readonly DIGITAL_NONE_OPTION = 'NONE';

    @Input() logic!: ElementConditionLogicViewData;

    isDigital: boolean;
    analogText: string;
    digitalStringList: string[];
    analogLogicList: string[];
    analogError: boolean = false;

    digitalString: string = '';

    isShowDialog: boolean = false;
    optionsTop: string;
    optionsLeft: string;
    private destroy$: Subject<void> = new Subject();

    @ViewChild('analogInput') analogInput: ElementRef;
    @ViewChild('digitalInput') digitalInput: ElementRef;
    @ViewChild('conditionELement') conditionELement: ElementRef;

    @Output() onChangeLogic = new EventEmitter<ElementConditionLogicViewData>();
    @Output() onFocus = new EventEmitter<ElementConditionLogicViewData>();
    @Output() onBlur = new EventEmitter<void>();

    constructor(private cdr: ChangeDetectorRef) {}

    @HostListener('document:click', ['$event'])
    clickOut(event: MouseEvent) {
        if (
            this.isShowDialog &&
            !this.analogInput?.nativeElement.contains(event.target) &&
            !this.digitalInput?.nativeElement.contains(event.target)
        ) {
            this.isShowDialog = false;
        }
    }

    ngOnInit(): void {
        const scrollContainer = document.querySelector('.content-editor-matrix');
        fromEvent(scrollContainer, 'scroll')
            .pipe(takeUntil(this.destroy$))
            .subscribe(event => {
                if (this.isShowDialog) this.isShowDialog = false;
            });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.logic && this.logic) {
            this.isDigital = ChannelFns.isDigital(this.logic?.channel);

            if (!this.isDigital) {
                if (this.logic.setToAlarm) {
                    this.analogText = this.ALARM_OPTION;
                } else {
                    if (this.logic.analogOperator == LOGIC_OPERATOR.BOTH) {
                        const numbers = this.logic.analogValue.match(/-?\d+(\.\d+)?/g);
                        const min = Number(numbers?.[0]);
                        const max = Number(numbers?.[1]);
                        this.analogText = `${min}<x<${max}`;
                    } else this.analogText = this.logic.analogOperator + this.logic.analogValue;
                }
                this.analogLogicList = [];
                if (this.logic.channel.fcuData.alarmSettingVeryHigh.enable) {
                    this.analogLogicList.push(`HH (<${this.logic.channel.fcuData.alarmSettingVeryHigh.setPoint})`);
                } else if (this.logic.channel.fcuData.alarmSettingHigh.enable) {
                    this.analogLogicList.push(`H (<${this.logic.channel.fcuData.alarmSettingHigh.setPoint})`);
                } else if (this.logic.channel.fcuData.normalRange.min || this.logic.channel.fcuData.normalRange.max) {
                    this.analogLogicList.push(
                        `Normal (${this.logic.channel.fcuData.normalRange.min}<x<${this.logic.channel.fcuData.normalRange.max})`
                    );
                } else if (this.logic.channel.fcuData.alarmSettingLow.enable) {
                    this.analogLogicList.push(`L (<${this.logic.channel.fcuData.alarmSettingLow.setPoint})`);
                } else if (this.logic.channel.fcuData.alarmSettingVeryLow.enable) {
                    this.analogLogicList.push(`LL (<${this.logic.channel.fcuData.alarmSettingVeryLow.setPoint})`);
                } else if (this.logic.channel.fcuData.alarmSettingSensorFail.enable) {
                    this.analogLogicList.push(this.SENSOR_FAILURE);
                }

                this.analogLogicList.push(this.ALARM_OPTION);
            } else {
                if (this.logic.setToAlarm) {
                    this.digitalString = this.ALARM_OPTION;
                } else {
                    this.digitalString = this.logic.digitalString;
                }

                this.digitalStringList = [
                    this.DIGITAL_NONE_OPTION,
                    this.logic.channel.fcuData.digitalAlarmSetting.statusStringContactOn,
                    this.logic.channel.fcuData.digitalAlarmSetting.statusStringContactOff,
                    this.ALARM_OPTION,
                ];
            }
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    openDialog() {
        this.updatePosition();
        if (!this.isDigital && this.analogText) this.isShowDialog = false;
        else this.isShowDialog = !this.isShowDialog;
    }

    setDigitalString(digitalString: string) {
        if (digitalString === this.ALARM_OPTION) {
            this.logic = {
                ...this.logic,
                digitalString: '',
                setToAlarm: true,
                isError: false,
            };
            this.digitalString = this.ALARM_OPTION;
        } else if (digitalString === this.DIGITAL_NONE_OPTION) {
            this.logic = {
                ...this.logic,
                digitalString: this.DIGITAL_NONE_OPTION,
                setToAlarm: false,
                isError: false,
            };
            this.digitalString = this.DIGITAL_NONE_OPTION;
        } else {
            this.logic = {
                ...this.logic,
                setToAlarm: false,
                digitalString,
                isError: false,
            };
            this.digitalString = digitalString;
        }

        this.onChangeLogic.emit(this.logic);
    }

    inputFocus() {
        this.onFocus.emit(this.logic);
    }

    setAnalogLogic(analogLogic: string) {
        if (analogLogic === this.SENSOR_FAILURE) {
            this.logic = {
                ...this.logic,
                analogOperator: '',
                setToAlarm: false,
                analogValue: this.SENSOR_FAILURE,
                isError: false,
            };
            this.analogText = this.logic.analogOperator + this.logic.analogValue;
            this.onChangeLogic.emit(this.logic);
        } else if (analogLogic === this.ALARM_OPTION) {
            this.logic = {
                ...this.logic,
                analogOperator: '',
                analogValue: '',
                setToAlarm: true,
                isError: false,
            };
            this.analogText = this.ALARM_OPTION;
            this.onChangeLogic.emit(this.logic);
        } else {
            try {
                const inside = analogLogic.match(/\(([^)]*)\)/)?.[1].trim() ?? analogLogic.trim();
                const { op, value } = this.parseExpr(inside);
                this.logic = {
                    ...this.logic,
                    setToAlarm: false,
                    analogOperator: op,
                    analogValue: value,
                    isError: false,
                };
                this.analogText = inside;
                this.onChangeLogic.emit(this.logic);
            } catch (error) {
                console.log(error);
            }
        }
    }

    analogInputBlur() {
        this.onChangeLogic.emit(this.logic);
        this.onBlur.emit();
    }

    digitalInputBlur() {
        this.onBlur.emit();
    }

    anaglogTextChange(value: string) {
        if (value) {
            this.isShowDialog = false;
        } else {
            this.isShowDialog = true;
        }

        try {
            if (value === this.ALARM_OPTION) {
                this.logic = {
                    ...this.logic,
                    analogOperator: '',
                    analogValue: '',
                    setToAlarm: true,
                    isError: false,
                };
                this.analogText = this.ALARM_OPTION;
            } else if (value === this.SENSOR_FAILURE) {
                this.logic = {
                    ...this.logic,
                    analogOperator: '',
                    setToAlarm: false,
                    analogValue: this.SENSOR_FAILURE,
                    isError: false,
                };
                this.analogText = this.logic.analogOperator + this.logic.analogValue;
            } else {
                const { op, value: analogValue } = this.parseExpr(value);
                this.logic = {
                    ...this.logic,
                    setToAlarm: false,
                    analogOperator: op,
                    analogValue,
                    isError: false,
                };
                if (op == LOGIC_OPERATOR.BOTH) {
                    const numbers = analogValue.match(/-?\d+(\.\d+)?/g);
                    const min = Number(numbers?.[0]);
                    const max = Number(numbers?.[1]);
                    if (isNaN(min) || isNaN(max)) this.logic.isError = true;
                    this.analogText = `${min}<x<${max}`;
                } else this.analogText = this.logic.analogOperator + this.logic.analogValue;
            }
        } catch (error) {
            this.logic.analogValue = value;
            this.logic.isError = true;
        }
    }

    parseExpr(input: string): { op: LogicOperator; value: string } {
        if (input === '') {
            return { op: '', value: '' };
        }

        const s = input.trim();
        const ops: LogicOperator[] = [LOGIC_OPERATOR.LT, LOGIC_OPERATOR.GT];
        const hasRange = input.includes('<x<');

        let op = ops.find(o => s.startsWith(o));

        if (!op) {
            if (!hasRange) throw new Error('Invalid number');
            else op = LOGIC_OPERATOR.BOTH;
        }

        const numStr = s.slice(op.length).trim();

        if (!hasRange && !/^[+-]?\d+(\.\d+)?$/.test(numStr)) {
            throw new Error('Invalid number');
        }
        let value: any = !hasRange ? Number(numStr) : 0;

        const numbers = input.match(/-?\d+(\.\d+)?/g);
        const min = Number(numbers?.[0]);
        const max = Number(numbers?.[1]);

        if (
            !hasRange &&
            (this.logic?.channel?.fcuData.measurableRange.max < value ||
                value < this.logic?.channel?.fcuData.measurableRange.min)
        ) {
            throw new Error('Exceeded the measurable range.');
        } else if (
            hasRange &&
            (min > this.logic.channel.fcuData.normalRange.min || max > this.logic.channel.fcuData.normalRange.max)
        ) {
            throw new Error('Exceeded the measurable range.');
        }

        if (hasRange) value = `${min} ${max}`;

        return { op, value: String(value) };
    }

    private updatePosition() {
        if (!this.conditionELement) return;
        const conditionPosition = this.conditionELement.nativeElement.getBoundingClientRect();
        const scrollTop = window.scrollY;
        const scrollLeft = window.scrollX;

        const top = conditionPosition.bottom + scrollTop + 3 - 20;

        this.optionsTop = top + 15 + 'px';
        this.optionsLeft = conditionPosition.left + scrollLeft + 'px';
        this.cdr.detectChanges();
    }
}
